import { useNavigation } from '@react-navigation/native';
import React, {
    useCallback,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';

import { loadBranchCapabilities } from '../../actions/providerCodes';
import { addCard } from '../../actions/user';
import { loadVisits } from '../../actions/visits';
import {
    BigButton,
    Page,
    Section,
    selectOptions,
    showNotification,
    SingleNavigation,
    TopNotification,
    VBox,
    View
} from '../../common';
import { ClinicSelector } from '../../common/Selector';
import { usePersonalData } from '../../hooks/usePersonalData';
import {
    actualNotificationsByTarget,
    getIsVisitsLoadOrders,
    getUser
} from '../../selectors';
import { getProviderCodesClinics } from '../../selectors/providerCodes';
import { dbGettr } from '../../selectors/utils';
import { isEmpty } from '../../utils';
import {
    GET_PAST_VISITS_FROM_FB,
    getOnlyPastVisits,
    updatePastVisits
} from '../App/snippets/Tests&Visits/utils';
import { LoadVisitConsents } from './LoadVisitConsents';

export const LoadVisits = Page.register(
    ({
        // enableLoadPastsVisits,
        user,
    }) => {
        const [ person, setPerson ] = useState();
        const removeVisitProvider = useSelector(dbGettr('removeVisitProvider'));
        // const [ visitors, setVisitors ] = useState([]);
        const explainId = 'titles.loadVisitsHowToWork';
        const explainText = Object.R(explainId);
        const [ selectedClinic, setSelectedClinic ] = useState({});
        const [ error, setError ] = useState(undefined);
        const [isConsentsComplete, setIsConsentsComplete] = useState(false);
        const newConsents = useRef(null);

        const dispatch = useDispatch();
        const isLoading = useSelector(getIsVisitsLoadOrders);
        const notifications = useSelector(actualNotificationsByTarget);
        const providerCodes = useSelector(getProviderCodesClinics);
        const navigation = useNavigation();
        const {
            changeConsent,
        } = usePersonalData();

        const visitors = useMemo(() => {
            if (!user?.info) {
                return [];
            }
            const relatives =
                user?.info?.relatives
                    ?.filter((item) => item?.isVisitsManageable)
                    .map(({
                        profileId,
                        name,
                        relation,
                        ...other
                    }) => ({
                        id: profileId,
                        name,
                        relation,
                        ...other,
                    })) ?? [];
            const me = user?.info?.me
                ? {
                    id: user?.info?.userId,
                    name: user?.info?.name,
                    birthday: user?.info?.birthday,
                    sex: user?.info?.sex,
                    phones: user?.info?.phones,
                    emails: user?.info?.emails,
                }
                : undefined;
            if (me && me?.name) {
                setPerson(me);
            } else {
                setPerson(relatives?.[0]);
            }
            return [ me, ...relatives ];
        }, [user?.info]);

        const selectVisitorHandler = () =>
            selectOptions({
                title: 'titles.loadVisitsForWhoVisit',
                data: visitors?.filter(({ name }) => name),
                selected: person?.id,
                onSelect: (params) => setPerson(params),
            });

        // useEffect(() => {
        //     setVisitors(getVisitors());
        // }, [ user ]);

        const setConsents = useCallback(() => {
            return new Promise((resolve) => {
                const consents = newConsents.current;
                if (consents === null) {
                    resolve(true);
                    return;
                }
                const providerCode = providerCodes?.[selectedClinic?.id]?.providerCode;
                dispatch(changeConsent({ providerCode, profileId: person?.id, consent: consents, onComplete: () => {
                    resolve(true);
                }, onError: (e) => {
                    setError(e?.message);
                    resolve(false);
                },
                onCancel: (r) => {
                    const type = r?.type;
                    if (type === 'error') {
                        setError(Object.R(r?.message));
                        resolve(false);
                    }
                    resolve(true);
                }}));
            });

        }, [changeConsent,
            dispatch,
            person?.id,
            providerCodes,
            selectedClinic]);

        const onLoadHandler = useCallback(async () => {
            const profiles = visitors
                ?.filter?.((item) => item?.id === person?.id)
                ?.map
                ?.(({
                    id,
                    name,
                    sex,
                    birthday,
                    phones = [],
                    emails = [],
                }) => ({
                    _id: id,
                    fullNameRu: name,
                    sex,
                    birthday,
                    phones: Array.isArray(phones) ? phones : [ phones ],
                    emails,
                }));
            const res = await setConsents();

            if (!res) {
                return;
            }

            dispatch(
                loadVisits({
                    profileId: person?.id ?? '',
                    clinicId: selectedClinic?.id ?? '',
                    onError: (e, fullError) => {
                        setError(
                            `${e}${fullError?.detail ? `. ${fullError?.detail}` : ''}`,
                        );
                    },
                    onSuccess: () => {
                        setError(undefined);
                        showNotification(`titles.loadVisitsSuccess`);
                        dispatch(addCard({ name: 'new data' }));
                        navigation.navigate('MyCard', { screen: 'Visit' });
                    },
                    profiles,
                    cb: (data) => {
                        if (!GET_PAST_VISITS_FROM_FB) {
                            const pastV = getOnlyPastVisits(data);
                            const filteredPastVisits = removeVisitProvider?.length
                                ? pastV?.filter(
                                    ({ providerCode }) =>
                                        !removeVisitProvider?.includes(providerCode),
                                )
                                : pastV;
                            updatePastVisits(filteredPastVisits);
                        }
                    },
                }),
            );
        }, [dispatch,
            navigation,
            person?.id,
            removeVisitProvider,
            selectedClinic?.id,
            setConsents,
            visitors]);

        const onSelectClinicHandler = useCallback((clinic) => {
            const { id } = clinic;
            console.log('Selected clinic', id);
            dispatch(loadBranchCapabilities({ branchId: id }));
            const notify = notifications(id);
            setError(notify?.message);
            setSelectedClinic(notify ? undefined : clinic);
        }, [ dispatch, notifications ]);

        const haveConsents = useMemo(() => {
            return isConsentsComplete;
        }, [ isConsentsComplete ]);

        return (
            <Page
                name="LoadVisits"
                isLoading={isLoading}
            >
                {!user?.isAuthenticated ? null : (
                    <>
                        {error ? (
                            <TopNotification
                                hint={{
                                    level: 'warn',
                                    message: `${error}`,
                                }}
                            />
                        ) : (
                            explainId !== explainText && <TopNotification hint={explainText}/>
                        )}
                        <VBox>
                            <Section
                                title="titles.loadVisitsForWhoVisit"
                                capitalize
                            >
                                <SingleNavigation
                                    title={`${person && person.name ? person.name : ' '}`}
                                    onPress={selectVisitorHandler}
                                />
                            </Section>
                            <Section
                                title="titles.loadVisitsClinic"
                                capitalize
                            >
                                <SingleNavigation
                                    title={`${
                                        isEmpty(selectedClinic)
                                            ? Object.R('titles.loadVisitsClinicSelect')
                                            : selectedClinic?.name
                                    }`}
                                    onPress={() =>
                                        Page.showModal(
                                            <ClinicSelector
                                                title={Object.R('titles.loadVisitsClinic')}
                                                onSelect={onSelectClinicHandler}
                                                filterFunction={(item) => item.onlineConnectivity}
                                            />,
                                        )
                                    }
                                />
                            </Section>
                            <LoadVisitConsents
                                profileId={person?.id}
                                branchId={selectedClinic?.id}
                                onComplete={(isDone, data) => {
                                    setIsConsentsComplete(isDone);
                                    newConsents.current = isDone ? data : null;
                                }}/>
                            <View style={{ paddingHorizontal: 10 }}>
                                <BigButton
                                    title="buttons.loadVisits"
                                    capitalize
                                    disabled={isEmpty(selectedClinic) || isEmpty(person) || !haveConsents}
                                    action={onLoadHandler}
                                />
                            </View>
                        </VBox>
                    </>
                )}
            </Page>
        );
    },
    { user: { from: getUser } },
);
